import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../../stores/authStore';
import { UserMenu } from './UserMenu';

interface HomeTopBarProps {
  /** 侧边栏折叠态：决定顶栏左缘偏移（72/240） */
  collapsed: boolean;
  /** 顶栏左端插槽（如预览页的返回按钮）：mr-auto 占位，不影响右缘胶囊组布局；
      pl-2 + 栏 px-4 = 内容区左缘 24px，对齐设计稿返回按钮 x=24 */
  leading?: React.ReactNode;
  /** 滚动感知变色开关（默认开）：顶栏默认透明无底色，页面滚动超过阈值后渐显
      #121212 底色（与首页背景一致）。仅 body 滚动的页面（home/canvas/detail/tools）开启；
      全屏无滚动的页面（featured 预览页 fixed inset-0 overflow-hidden，且文档
      scrollY 残留首页位置）须传 false 常透明，避免误显底色 */
  scrollAware?: boolean;
}

// 与 AppNew / Sidebar 一致的过渡缓动
const SPRING = 'cubic-bezier(0.32, 0.72, 0, 1)';

/** 顶栏设计坐标高度：设计稿 217:1229「顶部栏」高 72，栏内内容（登录按钮/
    UserMenu 胶囊组）高 40，上下各留 16px（设计稿按钮组 y=24）。供
    FeaturedPreviewPage 等页面计算顶栏让位/fit 基准引用，改动只改这一处 */
export const HOME_TOPBAR_H = 72;

/** 滚动变色阈值：滚过 8px 才算「页面已滚动」，滤掉回弹/微小抖动 */
const SCROLL_THRESHOLD = 8;

/**
 * 首页顶部用户栏（设计稿 217:1121「顶部栏」底色 #141414；高度按需求调整，
 * 设计稿原始 72 → 曾减至 60 → 现回归设计稿 72（按钮组 y=24，中心 44 与
 * 左边栏收起按钮中心对齐）。
 *
 * 底色行为：默认透明（无底色），页面滚动超过阈值后渐显 #121212——
 * （设计稿 217:1121 原为 #141414，按需求改为与首页背景色一致）
 * 顶栏为 fixed 常驻视口顶、不随文档滚动，滚动发生在 body，故监听
 * window.scrollY 判定；非滚动页面（featured 预览页）传 scrollAware=false 常透明。
 *
 * 放在 AppNew 层（而非 HomePage 内部）：顶栏为 fixed 常驻视口顶、不随文档滚动，
 * 由 AppNew 统一挂载并与侧边栏折叠态联动。
 *
 * 几何：left = 侧边栏宽（240/72）、right = 0 —— 栏宽即「侧边栏右侧剩余空间」，
 * 右缘始终贴合视口右缘（即梦式原生流式布局，无整页缩放，任意窗口宽度自适应）。
 */
export const HomeTopBar: React.FC<HomeTopBarProps> = ({ collapsed, leading, scrollAware = true }) => {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const openLoginModal = useAuthStore((s) => s.openLoginModal);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!scrollAware) {
      setScrolled(false);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    // 挂载即同步一次（返回首页等场景文档 scrollY 保留原位，直接呈现正确底色态）
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [scrollAware]);

  return (
    <div
      className="fixed top-0 z-[200]"
      style={{
        left: collapsed ? 72 : 240,
        right: 0,
        transition: `left 0.25s ${SPRING}`,
      }}
    >
      <div
        className="flex items-center justify-end px-4"
        style={{
          height: HOME_TOPBAR_H,
          /* 设计稿按钮组 y=24（40px 高 → 中心 44），非垂直居中：与左边栏收起按钮
             （top 32 / 24x24，中心 44）对齐。pt-16 使内容盒 16..72、40px 元素落位
             24..64、36px 元素落位 26..62，全部中心对齐 44 */
          paddingTop: 16,
          backgroundColor: scrolled ? '#121212' : 'transparent',
          transition: `background-color 0.3s ${SPRING}`,
        }}
      >
        {/* 左端插槽：mr-auto 把右缘胶囊组推到栏尾；pl-2 补足设计稿 x=24（栏 px-4 = 16 + 8） */}
        {leading && <div className="mr-auto pl-2">{leading}</div>}
        {/* 未登录：登录/注册按钮（217:1121 内 96x40、1px #363636 描边、圆角 12、14px Medium 白字，
            栏内 y=24..64，中心 44 与左边栏收起按钮中心对齐） */}
        {!isLoggedIn ? (
          <button
            onClick={() => openLoginModal()}
            className="flex h-10 w-24 cursor-pointer items-center justify-center rounded-xl text-sm font-medium text-white transition-opacity hover:opacity-80"
            style={{ border: '1px solid #363636' }}
          >
            登录/注册
          </button>
        ) : (
          /* 已登录：设计稿 217:1195 顶栏右缘胶囊组（积分/会员中心 + 头像昵称） */
          <UserMenu />
        )}
      </div>
    </div>
  );
};
